import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { CreateTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/inputs/create-ticket.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { userId } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { Logger } from '@app/core/logger/logger';
import { useCpmsBoCreateTicketMutation } from '@service/cpms-bo-ticket.service';

import { TicketCreateFormController } from './ticket-create-form/ticket-create-form.controller';

interface TicketCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const TicketCreateController = ({
  onCancel,
  onSuccess,
}: TicketCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const currentUserId = useAppSelector(userId);
  const [createTicket] = useCpmsBoCreateTicketMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const notifyError = (error: unknown): void => {
    Logger.error(error);
    toast.error(t('$.ticket-create.error'));
  };

  const handleError: SubmitErrorHandler<CreateTicketModel> = notifyError;

  const handleSubmit: SubmitHandlerWithDirtyFields<CreateTicketModel> = (
    values,
  ) => {
    if (!currentUserId) {
      // This route is only reachable once authenticated.slice has a userId
      // (see AuthenticatedController); this is defense-in-depth, not an
      // expected user-facing path.
      notifyError('Cannot create ticket: no authenticated user id');
      return;
    }

    setMultiStepFormState(MultiStepFormState.Loading);
    createTicket({ ...values, creatorId: currentUserId })
      .unwrap()
      .then(() => {
        toast.success(t('$.ticket-create.success'));
        onSuccess();
      })
      .catch(notifyError)
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <TicketCreateFormController
      onError={handleError}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      state={multiStepFormState}
    />
  );
};
